import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'

import { completeQuickRegistration, requestRegistrationCode } from '../api/auth'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { AuthScreenLayout } from '../components/auth/AuthScreenLayout'
import { RegisterAuthField } from '../components/auth/RegisterAuthField'
import { NOTIFICATIONS_CONSENT_CHECKBOX_LABEL, NOTIFICATIONS_CONSENT_PATH } from '../legal/notificationsConsent'
import { MARKETING_CONSENT_EXTERNAL_URL, PUBLIC_OFFER_PATH } from '../legal/publicOffer'
import {
  hasQuickRegistrationErrors,
  validateQuickRegistrationAccount,
  validateQuickRegistrationEmail,
  type QuickRegistrationErrors,
  type QuickRegistrationField,
} from '../utils/quickRegistrationValidation'

type RegisterStep = 'email' | 'account'

export const RegisterScreen = () => {
  const { session, loading: authLoading, login } = useAuth()
  const navigate = useNavigate()
  const [step, setStep] = useState<RegisterStep>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [consentOffer, setConsentOffer] = useState(true)
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [notificationsConsent, setNotificationsConsent] = useState(true)
  const [loading, setLoading] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<QuickRegistrationErrors>({})
  const [formError, setFormError] = useState<string | null>(null)

  const clearFieldError = (field: QuickRegistrationField) => {
    setFieldErrors((current) => {
      if (!current[field]) return current
      const next = { ...current }
      delete next[field]
      return next
    })
  }

  if (session) return <Navigate to="/" replace />

  const requestCode = async (event: FormEvent) => {
    event.preventDefault()
    const errors = validateQuickRegistrationEmail(email)
    setFieldErrors(errors)
    setFormError(null)
    if (hasQuickRegistrationErrors(errors)) return

    setLoading(true)
    try {
      await requestRegistrationCode(email)
      setStep('account')
      setCode('')
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : 'Не удалось отправить код')
    } finally {
      setLoading(false)
    }
  }

  const createAccount = async (event: FormEvent) => {
    event.preventDefault()
    const errors = validateQuickRegistrationAccount({ code, password, consentOffer })
    setFieldErrors(errors)
    setFormError(null)
    if (hasQuickRegistrationErrors(errors)) return

    setLoading(true)
    try {
      const result = await completeQuickRegistration({
        email,
        code: code.trim(),
        password,
        consent_offer: true,
        marketing_consent: marketingConsent,
        notifications_consent: notificationsConsent,
      })
      const redirectPath = await login(email, password)
      navigate(redirectPath || result.redirectPath, { replace: true })
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : 'Не удалось создать аккаунт')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthScreenLayout variant="register" compactRegister={step === 'email'} loading={authLoading}>
      <div className="market-auth__card market-auth__card--register">
        <header className="market-auth__card-header market-auth__card-header--compact">
          <h2 className="market-auth__card-title market-auth__card-title--compact">Регистрация</h2>
          <p className="market-auth__card-lead market-auth__card-lead--compact">
            {step === 'email'
              ? 'Сначала подтвердите электронную почту — остальные данные понадобятся, когда начнёте работу.'
              : 'Укажите код из письма и придумайте пароль.'}
          </p>
        </header>

        {step === 'email' ? (
          <form className="market-auth__form market-auth__form--register" noValidate onSubmit={(event) => void requestCode(event)}>
            <RegisterAuthField
              field="email"
              className="market-auth__form-span-full"
              error={fieldErrors.email}
              label={<>Электронная почта <span className="market-auth-field__required">*</span></>}
            >
              <input
                className="market-auth-field__input"
                type="email"
                autoComplete="email"
                placeholder="zakupki@company.ru"
                value={email}
                aria-invalid={fieldErrors.email ? true : undefined}
                onChange={(event) => {
                  setEmail(event.target.value)
                  clearFieldError('email')
                }}
              />
            </RegisterAuthField>
            {formError ? <p className="echat-error market-auth__error market-auth__form-span-full" role="alert">{formError}</p> : null}
            <button type="submit" className="echat-btn echat-btn--primary market-auth__submit market-auth__form-span-full" disabled={loading}>
              {loading ? 'Отправляем…' : 'Получить код'}
            </button>
          </form>
        ) : (
          <form className="market-auth__form market-auth__form--register" noValidate onSubmit={(event) => void createAccount(event)}>
            <div className="market-auth__form-span-full market-auth__code-sent">
              <span>Код отправлен на <strong>{email}</strong></span>
              <button
                type="button"
                className="market-auth__link-button"
                disabled={loading}
                onClick={() => {
                  setStep('email')
                  setCode('')
                  setFieldErrors({})
                  setFormError(null)
                }}
              >
                Изменить почту
              </button>
            </div>
            <RegisterAuthField
              field="code"
              error={fieldErrors.code}
              label={<>Код из письма <span className="market-auth-field__required">*</span></>}
            >
              <input
                className="market-auth-field__input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={code}
                aria-invalid={fieldErrors.code ? true : undefined}
                onChange={(event) => {
                  setCode(event.target.value.replace(/\D/g, ''))
                  clearFieldError('code')
                }}
              />
            </RegisterAuthField>
            <RegisterAuthField
              field="password"
              error={fieldErrors.password}
              label={<>Пароль <span className="market-auth-field__required">*</span></>}
            >
              <input
                className="market-auth-field__input"
                type="password"
                autoComplete="new-password"
                placeholder="Не менее 8 символов"
                value={password}
                aria-invalid={fieldErrors.password ? true : undefined}
                onChange={(event) => {
                  setPassword(event.target.value)
                  clearFieldError('password')
                }}
              />
            </RegisterAuthField>
            <div className="market-auth__register-actions market-auth__form-span-full">
              <div className="market-auth__legal-consents" aria-label="Согласия">
                <div className={fieldErrors.consentOffer ? 'market-auth-field--invalid' : ''}>
                  <label className="market-auth-checkbox market-auth-checkbox--consent">
                    <input
                      type="checkbox"
                      checked={consentOffer}
                      aria-invalid={fieldErrors.consentOffer ? true : undefined}
                      onChange={(event) => {
                        setConsentOffer(event.target.checked)
                        clearFieldError('consentOffer')
                      }}
                    />
                    <span>Принимаю <Link className="market-auth-checkbox__doc-link" to={PUBLIC_OFFER_PATH} target="_blank" rel="noopener noreferrer">Публичную оферту</Link></span>
                  </label>
                  {fieldErrors.consentOffer ? <span className="market-auth-field__error" role="alert">{fieldErrors.consentOffer}</span> : null}
                </div>
                <label className="market-auth-checkbox market-auth-checkbox--consent">
                  <input type="checkbox" checked={marketingConsent} onChange={(event) => setMarketingConsent(event.target.checked)} />
                  <span>Согласие на рекламные сообщения (<a className="market-auth-checkbox__doc-link" href={MARKETING_CONSENT_EXTERNAL_URL} target="_blank" rel="noopener noreferrer">ознакомиться</a>) — необязательно</span>
                </label>
                <label className="market-auth-checkbox market-auth-checkbox--consent">
                  <input type="checkbox" checked={notificationsConsent} onChange={(event) => setNotificationsConsent(event.target.checked)} />
                  <span>{NOTIFICATIONS_CONSENT_CHECKBOX_LABEL} (<Link className="market-auth-checkbox__doc-link" to={NOTIFICATIONS_CONSENT_PATH} target="_blank" rel="noopener noreferrer">ознакомиться</Link>)</span>
                </label>
              </div>
              {formError ? <p className="echat-error market-auth__error" role="alert">{formError}</p> : null}
              <button type="submit" className="echat-btn echat-btn--primary market-auth__submit" disabled={loading}>
                {loading ? 'Создаём аккаунт…' : 'Создать аккаунт'}
              </button>
            </div>
          </form>
        )}

        <div className="market-auth__footer market-auth__footer--register">
          <p className="market-auth__signup market-auth__signup--in-card">Уже есть аккаунт? <Link className="market-auth__signup-link" to="/login">Войти</Link></p>
        </div>
      </div>
    </AuthScreenLayout>
  )
}
